import Box from '@mui/material/Box'
import Skeleton from '@mui/material/Skeleton'
import Stack from '@mui/material/Stack'
import Typography from '@mui/material/Typography'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import AutorenewRoundedIcon from '@mui/icons-material/AutorenewRounded'
import { GlassCard, GlassOutlineButton } from './Glass'
import PollDesign from './PollDesign'
import PollNavButton from './PollNavButton'
import ViewNavigation from './ViewNavigation'

export default function PollLoadingView({
  onBack,
  backLabel = 'Polls',
  onNewPoll,
  statusLabel,
}: {
  onBack?: () => void
  backLabel?: string
  onNewPoll?: () => void
  statusLabel?: string
}) {
  const surface = {
    p: { xs: 2, sm: 3 },
    background: '#ffffff',
    borderColor: '#efddc9',
    boxShadow: '0 3px 0 #efddc9',
    '& .MuiSkeleton-root': { backgroundColor: '#f1e6da' },
    '@media (prefers-reduced-motion: reduce)': {
      '& .MuiSkeleton-root::after': { animation: 'none' },
    },
  }
  return (
    <PollDesign>
      <Stack
        spacing={2}
        pb={2}
        sx={{ minWidth: 0, '& > .rt-view-navigation + *': { mt: '10px' } }}
      >
        <ViewNavigation mb={0}>
          <GlassOutlineButton
            onClick={onBack}
            startIcon={<ArrowBackRoundedIcon />}
          >
            {backLabel}
          </GlassOutlineButton>
          <PollNavButton
            disabled
            onClick={onNewPoll}
            startIcon={!statusLabel ? <AutorenewRoundedIcon /> : undefined}
          >
            {statusLabel || 'New Poll'}
          </PollNavButton>
        </ViewNavigation>
        <Box role="status" aria-label="Loading poll" aria-busy="true">
          <Stack spacing={2}>
            <GlassCard sx={surface}>
              <Typography
                variant="overline"
                sx={{ color: '#855c3d', fontWeight: 700, letterSpacing: 1 }}
              >
                Loading poll…
              </Typography>
              <Box display="flex" alignItems="center" gap={2} mt={1}>
                <Box
                  component="img"
                  src="/brand/random-poll.svg"
                  alt=""
                  aria-hidden
                  sx={{ width: { xs: 44, sm: 60 }, flexShrink: 0 }}
                />
                <Box flex={1} minWidth={0}>
                  <Skeleton animation="wave" width="85%" height={36} />
                  <Skeleton animation="wave" width="62%" height={24} />
                </Box>
              </Box>
              <Box
                display="grid"
                gridTemplateColumns="repeat(3, minmax(0, 1fr))"
                gap={1}
                mt={2}
              >
                {[0, 1, 2].map((index) => (
                  <Box
                    key={index}
                    sx={{
                      p: 1.25,
                      background: '#fffaf5',
                      borderRadius: '14px',
                    }}
                  >
                    <Skeleton animation="wave" width="65%" height={24} />
                    <Skeleton animation="wave" width="45%" height={16} />
                  </Box>
                ))}
              </Box>
            </GlassCard>
            <GlassCard sx={surface}>
              <Skeleton
                animation="wave"
                width="40%"
                height={28}
                sx={{ mb: 1.5 }}
              />
              <Stack spacing={1}>
                {Array.from({ length: 10 }, (_, index) => (
                  <Box
                    key={index}
                    display="flex"
                    alignItems="center"
                    gap={1.5}
                    sx={{
                      p: 1.25,
                      border: '1px solid #f1e6da',
                      borderRadius: '16px',
                    }}
                  >
                    <Skeleton
                      animation="wave"
                      variant="rounded"
                      width={28}
                      height={28}
                      sx={{ borderRadius: '9px', flexShrink: 0 }}
                    />
                    <Skeleton
                      animation="wave"
                      width={`${70 - (index % 4) * 9}%`}
                      height={24}
                    />
                  </Box>
                ))}
              </Stack>
            </GlassCard>
          </Stack>
        </Box>
      </Stack>
    </PollDesign>
  )
}
